import { supabase } from "./supabase"; // Asumsi client Supabase Anda diekspor dari file ini

// --- DEFINISI TIPE ---

/**
 * Interface untuk data profil pengguna yang akan diambil melalui JOIN.
 */
interface UserProfile {
  id: string;
  name: string;
  avatar_url: string | null;
}

/**
 * Interface untuk struktur komentar yang akan digunakan di frontend.
 * Menggabungkan data komentar dari tabel 'comments' dan data profil dari JOIN.
 */
export interface Comment {
  id: string;
  created_at: string;
  content: string;
  user_id: string;
  // Supabase JOIN akan mengembalikan objek user_profiles di sini
  user_profiles: UserProfile;
}

// Kolom yang akan diambil dari database, termasuk JOIN ke user_profiles
const COMMENT_SELECT_FIELDS = `
  id, 
  created_at, 
  content, 
  user_id,
  user_profiles(id, name, avatar_url)
`;

// --- FUNGSI UTAMA ---

/**
 * Mengambil hanya beberapa komentar terbaru (default 3) untuk suatu Lesson Plan.
 * @param lessonPlanId ID dari Lesson Plan yang komentarnya akan diambil.
 * @param limit Jumlah komentar yang akan diambil.
 * @returns Array of Comment (komentar terbaru).
 */
export async function fetchLatestComments(
  lessonPlanId: string,
  limit: number = 3
): Promise<Comment[]> {
  // Use getUser() which automatically refreshes tokens if expired
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) {
    throw new Error("Sesi tidak valid. Silakan login kembali.");
  }

  const { data, error } = await supabase
    .from("comments_lesson")
    .select(COMMENT_SELECT_FIELDS)
    .eq("lesson_plan_id", lessonPlanId)
    .order("created_at", { ascending: false }) // Urutkan dari yang terbaru
    .limit(limit); // Batasi jumlah data

  if (error) {
    console.error("Error fetching latest comments:", error);
    throw new Error(error.message);
  }

  // Karena kita mengurutkan dari terbaru, kita balik lagi untuk ditampilkan di UI (terlama di atas)
  return data as Comment[];
}

/**
 * Mengambil semua komentar untuk suatu Lesson Plan.
 * @param lessonPlanId ID dari Lesson Plan yang komentarnya akan diambil.
 * @returns Array of Comment (semua komentar, diurutkan dari yang terlama).
 */
export async function fetchAllComments(
  lessonPlanId: string
): Promise<Comment[]> {
  // Use getUser() which automatically refreshes tokens if expired
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) {
    throw new Error("Sesi tidak valid. Silakan login kembali.");
  }

  const { data, error } = await supabase
    .from("comments_lesson")
    .select(COMMENT_SELECT_FIELDS)
    .eq("lesson_plan_id", lessonPlanId)
    .order("created_at", { ascending: false }); // Urutkan dari yang terlama ke terbaru

  if (error) {
    console.error("Error fetching all comments:", error);
    throw new Error(error.message);
  }

  return data as Comment[];
}

/**
 * Menghitung total jumlah komentar untuk suatu Lesson Plan.
 * @param lessonPlanId ID dari Lesson Plan yang komentarnya akan dihitung.
 * @returns Jumlah total komentar.
 */
export async function countComments(lessonPlanId: string): Promise<number> {
  // Use getUser() which automatically refreshes tokens if expired
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) {
    throw new Error("Sesi tidak valid. Silakan login kembali.");
  }

  const { count, error } = await supabase
    .from("comments_lesson")
    .select("id", { count: "exact", head: true }) // Gunakan head: true untuk efisiensi
    .eq("lesson_plan_id", lessonPlanId);

  if (error) {
    console.error("Error counting comments:", error);
    throw new Error(error.message);
  }

  return count ?? 0;
}

/**
 * Menambahkan komentar baru ke Lesson Plan.
 * @param lessonPlanId ID dari Lesson Plan target.
 * @param userId ID pengguna yang membuat komentar.
 * @param content Isi teks komentar.
 */
export async function addComment(
  lessonPlanId: string,
  userId: string,
  content: string
): Promise<void> {
  // Use getUser() which automatically refreshes tokens if expired
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) {
    throw new Error("Sesi tidak valid. Silakan login kembali.");
  }

  const { error } = await supabase.from("comments_lesson").insert([
    {
      lesson_plan_id: lessonPlanId,
      user_id: userId,
      content: content,
    },
  ]);

  if (error) {
    console.error("Error adding comment:", error);
    throw new Error("Gagal memposting komentar: " + error.message);
  }
}
